import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CalendarPlus, Share2, Clock, CalendarDays, MapPin, Ticket, Navigation, Store, Heart, Users, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/Seo";
import { Overline, EventCard, img } from "@/components/Cards";
import { toast } from "sonner";

function parseTime(t) {
  // best-effort parse of a start time like "6:00 PM" or "18:00"
  if (!t) return null;
  const m = String(t).match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ap = (m[3] || "").toLowerCase();
  if (ap === "pm" && h < 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  return { h, min };
}

function googleCalUrl(e) {
  if (!e.date) return null;
  const d = e.date.replace(/-/g, "");
  const st = parseTime(e.time);
  let dates;
  if (st) {
    const pad = (n) => String(n).padStart(2, "0");
    const start = `${d}T${pad(st.h)}${pad(st.min)}00`;
    const endH = (st.h + 2) % 24;
    const end = `${d}T${pad(endH)}${pad(st.min)}00`;
    dates = `${start}/${end}`;
  } else {
    dates = `${d}/${d}`; // all-day
  }
  const eventUrl = `${window.location.origin}/events/${e.slug}`;
  const details = `${e.description || ""}\n\n📍 Discover more RiNo events & plan your visit at Visit RiNo:\n${eventUrl}\n\nExplore the River North Art District → ${window.location.origin}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `${e.title} — Visit RiNo`,
    details,
    location: e.address || "River North Art District, Denver, CO",
    dates,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function EventDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isCustomer = user && user.role === "customer";
  const [e, setE] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [interested, setInterested] = useState(false);
  const [count, setCount] = useState(0);
  const [myStatus, setMyStatus] = useState(null);

  useEffect(() => {
    setE(null); setNotFound(false);
    api.get(`/events/${slug}`).then((r) => { setE(r.data); setCount(r.data.interested_count || 0); }).catch(() => setNotFound(true));
    setInterested(!!localStorage.getItem(`interested_${slug}`));
    if (isCustomer) api.get("/me/events").then((r) => { const m = r.data.find((x) => x.slug === slug); if (m) setMyStatus(m.my_status); }).catch(() => {});
  }, [slug, isCustomer]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-400">Event not found. <Link to="/events" className="text-[#FF0055]">Back to events</Link></div>;
  if (!e) return <div className="py-32 text-center text-neutral-500">Loading...</div>;

  const dateObj = e.date ? new Date(e.date + "T00:00:00") : null;
  const dateLabel = dateObj ? dateObj.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "Date TBA";
  const gcal = googleCalUrl(e);
  const mapsUrl = e.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.address)}` : null;

  const setStatus = async (status) => {
    const { data } = await api.post(`/me/events/${slug}`, { status });
    setMyStatus(status === "none" ? null : status);
    setCount(data.interested_count);
    toast.success(status === "none" ? "Removed from your events" : `Marked as ${status}`);
  };

  const markInterested = async () => {
    if (isCustomer) { setStatus(myStatus === "going" ? "going" : "interested"); return; }
    if (interested) return;
    try {
      const { data } = await api.post(`/events/${slug}/rsvp`, {});
      setCount(data.interested_count);
      setInterested(true);
      localStorage.setItem(`interested_${slug}`, "1");
      toast.success(data.message);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const share = async () => {
    const url = window.location.href;
    const payload = { title: `${e.title} — Visit RiNo`, text: e.description?.slice(0, 120), url };
    if (navigator.share) { try { await navigator.share(payload); } catch {} }
    else { navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: e.title,
    description: e.description,
    ...(e.image ? { image: [e.image] } : {}),
    ...(e.date ? { startDate: e.date } : {}),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: e.venue || "River North Art District",
      address: e.address || "River North Art District, Denver, CO",
    },
    ...(e.price ? { offers: { "@type": "Offer", price: e.price, priceCurrency: "USD" } } : {}),
    organizer: { "@type": "Organization", name: "Visit RiNo", url: "https://visitrino.com" },
  };

  return (
    <div data-testid="event-detail">
      <Seo title={e.title} description={e.description?.slice(0, 155)} image={e.image} type="article" jsonLd={jsonLd} canonicalPath={`/events/${e.slug}`} />

      <div className="relative h-[44vh] overflow-hidden grain">
        <img src={img(e.image)} alt={e.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-8">
            <Link to="/events" className="flex w-fit items-center gap-2 text-neutral-300 hover:text-white text-sm mb-4" data-testid="back-to-events"><ArrowLeft size={16} /> Events</Link>
            <div><Overline>{e.category}</Overline></div>
            <h1 className="font-display font-black text-4xl sm:text-6xl text-white mt-2" data-testid="event-title">{e.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-neutral-200 text-sm mt-3">
              <span className="flex items-center gap-2"><CalendarDays size={16} className="text-[#FF0055]" /> {dateLabel}</span>
              {e.time && <span className="flex items-center gap-2"><Clock size={16} className="text-[#FF0055]" /> {e.time}</span>}
              {e.price && <span className="flex items-center gap-2 text-[#FFEA00]"><Ticket size={16} /> {e.price}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display font-bold text-2xl text-white mb-3">About this event</h2>
          <p className="text-neutral-300 text-lg leading-relaxed whitespace-pre-line" data-testid="event-description">{e.description || "Details coming soon."}</p>

          {e.business && (
            <div className="mt-10">
              <h2 className="font-display font-bold text-2xl text-white mb-4">Hosted at</h2>
              <Link to={`/directory/${e.business.slug}`} data-testid="event-venue-link"
                className="flex items-center gap-4 border border-white/10 bg-[#141414] p-4 hover:border-[#00E5FF] transition-colors group">
                <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
                  <img src={img(e.business.image)} alt={e.business.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div>
                  <div className="text-[#00E5FF] text-xs font-accent flex items-center gap-1"><Store size={13} /> {e.business.category}</div>
                  <div className="text-white font-display font-bold text-lg mt-0.5 group-hover:text-[#00E5FF] transition-colors">{e.business.name}</div>
                  {e.business.address && <div className="text-neutral-500 text-sm mt-0.5">{e.business.address}</div>}
                </div>
              </Link>
            </div>
          )}

          {e.more_events?.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display font-bold text-2xl text-white mb-4">More RiNo events</h2>
              <div className="grid grid-cols-1 gap-5" data-testid="more-events">
                {e.more_events.map((ev, i) => <EventCard key={ev.id || ev.slug} e={ev} index={i} />)}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-32 border border-white/10 bg-[#141414] p-6 space-y-5">
            <div className="flex items-center gap-2 text-white">
              <Users size={18} className="text-[#00E5FF]" />
              <span className="font-display font-extrabold text-2xl" data-testid="interested-count">{count}</span>
              <span className="text-neutral-400 text-sm">interested</span>
            </div>
            <button onClick={markInterested} disabled={interested && !isCustomer} data-testid="interested-btn"
              className={`w-full py-3 font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${(interested || myStatus) ? "bg-[#00FF66] text-black" : "bg-[#FF0055] hover:bg-[#ff2d74] text-white"}`}>
              {(interested || myStatus) ? <><Check size={16} /> {isCustomer && myStatus ? (myStatus === "going" ? "You're Going!" : myStatus === "went" ? "You Went" : "Interested") : "You're Interested!"}</> : <><Heart size={16} /> I'm Interested</>}
            </button>
            {isCustomer && (
              <div className="flex gap-1.5" data-testid="event-status-picker">
                {[["interested", "Interested"], ["going", "Going"], ["went", "Went"]].map(([v, l]) => (
                  <button key={v} onClick={() => setStatus(v)} data-testid={`event-status-${v}`}
                    className={`flex-1 py-1.5 text-xs border transition-colors ${myStatus === v ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/15 text-neutral-400 hover:text-white"}`}>{l}</button>
                ))}
              </div>
            )}

            <div className="space-y-3 text-sm border-t border-white/10 pt-5">
              <div className="flex items-start gap-3"><CalendarDays size={17} className="text-[#FF0055] mt-0.5 flex-shrink-0" /><div className="text-neutral-200">{dateLabel}{e.time ? <div className="text-neutral-500">{e.time}</div> : null}</div></div>
              {(e.venue || e.address) && (
                <div className="flex items-start gap-3">
                  <MapPin size={17} className="text-[#FF0055] mt-0.5 flex-shrink-0" />
                  <div className="text-neutral-200">
                    {e.venue && (e.business?.slug
                      ? <Link to={`/directory/${e.business.slug}`} data-testid="event-venue-name-link" className="text-[#00E5FF] hover:underline font-medium">{e.venue}</Link>
                      : <div>{e.venue}</div>)}
                    {e.address && <div className="text-neutral-500">{e.address}</div>}
                  </div>
                </div>
              )}
              {e.price && <div className="flex items-start gap-3"><Ticket size={17} className="text-[#FF0055] mt-0.5 flex-shrink-0" /><div className="text-[#FFEA00]">{e.price}</div></div>}
            </div>

            {e.ticket_url && (
              <a href={e.ticket_url} target="_blank" rel="noopener noreferrer" data-testid="tickets-btn"
                className="w-full py-3 bg-[#FFEA00] hover:bg-[#fff04d] text-black text-sm font-bold flex items-center justify-center gap-2 transition-colors">
                <Ticket size={16} /> Get Tickets
              </a>
            )}
            {gcal && (
              <a href={gcal} target="_blank" rel="noopener noreferrer" data-testid="add-calendar-btn"
                className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
                <CalendarPlus size={16} /> Add to Calendar
              </a>
            )}
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="directions-btn"
                className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
                <Navigation size={16} /> Get Directions
              </a>
            )}
            <button onClick={share} data-testid="share-btn"
              className="w-full py-3 border border-white/20 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
              <Share2 size={16} /> Share
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
